import Reveal from './ui/Reveal';
import Icon from './ui/Icon';
import Button from './ui/Button';
import SectionHeading from './ui/SectionHeading';
import { aiCapabilities, aiFlow } from '../data/content';

const AIFlow = () => (
  <div className="card p-6 sm:p-7" role="group" aria-labelledby="ai-flow-title">
    <p id="ai-flow-title" className="font-mono text-xs uppercase tracking-[0.16em] text-subtle">
      How an AI feature fits into your product
    </p>
    <ol className="mt-6">
      {aiFlow.map((step, i) => {
        const last = i === aiFlow.length - 1;
        return (
          <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
            {!last && (
              <span
                className="absolute left-[15px] top-8 h-[calc(100%-2rem)] w-px bg-gradient-to-b from-indigo-300/40 to-white/5"
                aria-hidden="true"
              />
            )}
            <span
              className={`relative z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border font-mono text-xs font-medium ${
                i === 2
                  ? 'border-indigo-300/40 bg-indigo-400/15 text-indigo-200'
                  : 'border-white/15 bg-ink-800 text-muted'
              }`}
            >
              {i + 1}
            </span>
            <div>
              <p className="text-[15px] font-semibold text-fg">{step.title}</p>
              <p className="mt-0.5 text-sm leading-relaxed text-muted">{step.text}</p>
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);

const AICapabilities = () => (
  <section id="ai" className="section border-t border-white/[0.06]" aria-labelledby="ai-title">
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-[520px] opacity-70"
      style={{ background: 'radial-gradient(60% 100% at 20% 0%, rgba(99,102,241,0.14), transparent 70%)' }}
      aria-hidden="true"
    />
    <div className="container-page relative grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
      <div className="lg:sticky lg:top-28 lg:self-start">
        <SectionHeading
          id="ai-title"
          eyebrow="AI capabilities"
          title="Build Smarter With AI"
          lead="AI works best when it’s built into a product people already use. I add practical AI features to websites and business applications: assistants that answer questions, tools that draft content, and workflows that take repetitive work off your team."
        />
        <Reveal className="mt-8">
          <AIFlow />
        </Reveal>
        <Reveal className="mt-8 flex flex-col items-start gap-3">
          <p className="max-w-md text-sm leading-relaxed text-muted">
            Have a specific idea? Tell me the workflow you want to improve and I’ll tell you honestly whether AI is the
            right tool for it.
          </p>
          <Button href="#contact" arrow>
            Discuss an AI idea
          </Button>
        </Reveal>
      </div>

      <ul className="grid gap-3.5">
        {aiCapabilities.map((item, i) => (
          <Reveal as="li" key={item.title} delay={i * 45}>
            <div className="card card-hover flex items-start gap-4 p-5">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border border-indigo-300/20 bg-indigo-400/10 text-indigo-300">
                <Icon name={item.icon} className="h-[18px] w-[18px]" />
              </span>
              <div>
                <h3 className="text-base font-semibold tracking-tight text-fg">{item.title}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted">{item.description}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export default AICapabilities;
